import { useAccount } from 'context/AccountContext';
import { useOrganization } from 'context/OrganizationContext';
import { CancelablePromise, OrdersService, SupplierOrderDetail, UsageTypeEnum } from 'openapi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'ui/Alert';
import { today } from 'utilities/date.utilities';
import { Severity } from 'utilities/severity';

interface ReturnType {
  parentOrder: SupplierOrderDetail | undefined;
  infoElement: JSX.Element;
}

interface Props {
  selectedMareUid: string | undefined;
  open: boolean;
}

/**
 * Small hook to handle the breeding plan selection for the semen order
 */
function useMareCycleParentOrderItem({ selectedMareUid, open }: Props): ReturnType {
  const [parentOrder, setParentOrder] = useState<SupplierOrderDetail>();

  const { selectedOrganizationUid } = useOrganization();
  const { t } = useTranslation();
  const { formatDate } = useAccount();

  /**
   * Find a parent order for this given order
   * When we found a parent item, we will mark this order as repeated
   */
  const findParentOrderItem = useCallback((organizationUid: string, mareUid: string): CancelablePromise<SupplierOrderDetail[]> => {
    const promise = OrdersService.ordersSuppliedFindParentSemenOrdersCreate({
      supplierUid: organizationUid,
      requestBody: {
        mare_uid: mareUid,
        date: today(),
        usage_type: UsageTypeEnum.KI,
        exclude_children: true,
      },
    });
    promise
      .then(res => setParentOrder(res[0]))
      .catch(error => {
        // we ignore the error but only log it
        console.error(error);
      });

    return promise;
  }, []);

  const infoElement = useMemo(() => {
    if (!parentOrder) {
      return <></>;
    }
    return (
      <Alert
        className='mb-2'
        severity={Severity.Info}
        message={t(
          'mare-cycle-previous-order-message',
          'The previous order for this mare on {{date}} included this stallion, shipment, and insemination station.',
          {
            date: formatDate(new Date(Date.parse(parentOrder?.created_on))),
          },
        )}
      />
    );
  }, [formatDate, parentOrder, t]);

  /**
   * Find the parent order IDs so we can set a repeat order
   */
  useEffect(() => {
    if (selectedOrganizationUid && selectedMareUid && open) {
      const promise = findParentOrderItem(selectedOrganizationUid, selectedMareUid);
      return () => promise.cancel();
    } else {
      setParentOrder(undefined);
    }
  }, [findParentOrderItem, selectedMareUid, selectedOrganizationUid, open]);

  return {
    parentOrder,
    infoElement,
  };
}

export default useMareCycleParentOrderItem;
